import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {SupportChatApi} from './supportChatApi';
import {SupportChatService} from './SupportChatService';
import {SupportChatPanel} from './SupportChatPanel';
import {SupportIncomingCard, incomingFromText} from './SupportIncomingCard';
import {WebSocketChannels, WebSocketEvents, WebSocketsClient} from '../../websockets/WebSocketsClient';
import './SupportChat.scss';

// SupportChatBubble — botón flotante abajo-derecha (donde vivía el Messenger de Intercom) +
// badge de no-leídos + panel montado on-demand (Reemplazo de Intercom, Fase 3).
//
// - Se suscribe a SupportChatService: 'open' (de un CTA showNewMessage) abre el panel; 'close' lo cierra.
// - Escape cierra el panel cuando está abierto.
// - TARJETA de mensaje del equipo (SupportIncomingCard): cuando el equipo le escribió a ESTE usuario
//   y todavía no lo leyó, aparece sola junto a la burbuja (con el panel cerrado). Se entera por
//   `GET /support/incoming/`, que se pide al montar, al volver a la pestaña, cada minuto con la
//   pestaña visible, al cerrar el panel y en el acto cuando llega la señal en vivo. La señal viaja
//   por la conexión de Pusher del TABLERO sólo si ya está abierta (`bindWhenConnected`): escuchar
//   siempre la cuenta de soporte costaría una conexión por pestaña de una cuenta free de 100.
// - El BADGE sale de la misma respuesta (`unread_conversations`). `GET /support/unread-count/` queda
//   sólo de respaldo cuando `incoming/` falla (un backend sin el endpoint): pedirlo además, cada
//   3 minutos y con la pestaña oculta, repetía el número y hacía que una pestaña olvidada contara
//   como actividad del usuario.
//
// La burbuja se monta una vez por usuario (`key` en SupportChatEntry), así que nada de este estado
// pasa de una sesión a la siguiente.

const INCOMING_POLL_MS = 60 * 1000; // 1 min, sólo con la pestaña visible

function isTabVisible() {
    return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function messageIdOf(item) {
    return item && item.message ? item.message.id : null;
}

export const SupportChatBubble = ({agencyId}) => {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [incoming, setIncoming] = useState([]);
    // Mensajes que el usuario apartó con Escape: no vuelven hasta que llegue otro (o recargue).
    const [dismissedMessageIds, setDismissedMessageIds] = useState(() => new Set());
    // Mensajes que el usuario ya resolvió («Entendido» o «Responder»). Una consulta que salió ANTES
    // del clic vuelve todavía con ellos: sin este filtro la tarjeta se iba, volvía y se iba. Se
    // olvidan cuando el servidor deja de devolverlos.
    const [handledMessageIds, setHandledMessageIds] = useState(() => new Set());
    // Al cerrar el panel la tarjeta espera la consulta: lo que había antes de abrirlo puede estar
    // ya leído, y pintarlo hasta que vuelva el GET era un parpadeo del mensaje viejo.
    const [holdCard, setHoldCard] = useState(false);

    const openRef = useRef(open);
    useEffect(() => { openRef.current = open; }, [open]);
    const buttonRef = useRef(null);
    // Sólo cuenta la respuesta de la ÚLTIMA consulta: una vieja que llega tarde no pisa a la nueva.
    const lastRequestRef = useRef(0);
    const inFlightRef = useRef(false);

    const fetchUnread = useCallback(() => {
        SupportChatApi.fetchUnreadCount().then((data) => {
            // El backend devuelve `unread_conversations` (contrato /support/unread-count/).
            if (data && data.status === 'OK' && typeof data.unread_conversations === 'number') {
                setUnreadCount(data.unread_conversations);
            }
        });
    }, []);

    const fetchIncoming = useCallback(() => {
        const request = ++lastRequestRef.current;
        inFlightRef.current = true;
        return SupportChatApi.fetchIncoming().then((data) => {
            if (request !== lastRequestRef.current) return;
            inFlightRef.current = false;
            setHoldCard(false);
            if (data && data.status === 'OK' && Array.isArray(data.incoming)) {
                setIncoming(data.incoming);
                const served = new Set(data.incoming.map(messageIdOf));
                setHandledMessageIds((prev) => new Set([...prev].filter((id) => served.has(id))));
                if (typeof data.unread_conversations === 'number') setUnreadCount(data.unread_conversations);
                return;
            }
            // Un 4xx dice que esta sesión ya no puede ver esos mensajes (se cerró, cambió de agencia):
            // no se muestra nada. Un corte de red o un 5xx es un refresco de fondo que falló: queda
            // lo que había, sin toast ni estado de error.
            const httpStatus = data && data.http_status;
            if (httpStatus >= 400 && httpStatus < 500) setIncoming([]);
            fetchUnread();
        });
    }, [fetchUnread]);

    // ── Mensajes del equipo: al montar, al volver a la pestaña, cada minuto y en vivo ──
    useEffect(() => {
        fetchIncoming();
        const timer = setInterval(() => {
            if (isTabVisible()) fetchIncoming();
        }, INCOMING_POLL_MS);
        // Volver a la pestaña dispara `focus` y `visibilitychange` juntos: una sola consulta.
        const onVisible = () => {
            if (isTabVisible() && !inFlightRef.current) fetchIncoming();
        };
        window.addEventListener('focus', onVisible);
        document.addEventListener('visibilitychange', onVisible);
        const unbind = WebSocketsClient.bindWhenConnected(
            WebSocketChannels.BU_DASHBOARD_SYNC, WebSocketEvents.SUPPORT_INCOMING, fetchIncoming);
        return () => {
            clearInterval(timer);
            window.removeEventListener('focus', onVisible);
            document.removeEventListener('visibilitychange', onVisible);
            unbind();
        };
    }, [fetchIncoming]);

    // ── Suscripción al service (CTAs showNewMessage → open) ──────────────────────
    useEffect(() => {
        const unsub = SupportChatService.subscribe((event) => {
            if (event.type === 'open') {
                setOpen(true);
            } else if (event.type === 'close') {
                setOpen(false);
            }
        });
        // Reconciliar con el estado del service por si ya estaba abierto al montar, o si la URL
        // trajo `?support_conversation=` (el link del mail): abrir el chat en esa conversación.
        if (SupportChatService.isPanelOpen() || SupportChatService.hasPendingConversation()) setOpen(true);
        return unsub;
    }, []);

    // ── Escape cierra el panel abierto ───────────────────────────────────────────
    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closePanel();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const openPanel = () => {
        SupportChatService.openPanel();
        setOpen(true);
    };

    const closePanel = () => {
        SupportChatService.closePanel();
        setOpen(false);
        // Al cerrar, lo leído en el panel cambia la tarjeta y el badge → se vuelve a consultar.
        setHoldCard(true);
        fetchIncoming();
    };

    const toggle = () => {
        if (openRef.current) {
            closePanel();
        } else {
            openPanel();
        }
    };

    // Si el foco estaba en la tarjeta que se va, vuelve a la burbuja: si no, cae al `body` y quien
    // usa el teclado tiene que tabular desde el principio de la página.
    const keepFocusOutOfLeavingCard = () => {
        const active = document.activeElement;
        if (active && active.closest && active.closest('.support-incoming-card') && buttonRef.current) {
            buttonRef.current.focus();
        }
    };

    const markHandled = (item, handled) => {
        const messageId = messageIdOf(item);
        setHandledMessageIds((prev) => {
            const next = new Set(prev);
            if (handled) next.add(messageId); else next.delete(messageId);
            return next;
        });
    };

    // «Responder»: el chat se abre en esa conversación (el hilo la marca como leída al cargar).
    const replyToIncoming = (item) => {
        markHandled(item, true);
        SupportChatService.openConversation(item.conversation_id, item.conversation_status || null);
        setOpen(true);
    };

    // «Entendido»: leída, sin abrir el chat. No vuelve al recargar.
    const acknowledgeIncoming = (item) => {
        keepFocusOutOfLeavingCard();
        markHandled(item, true);
        SupportChatApi.markRead(item.conversation_id).then((data) => {
            if (!data || data.status !== 'OK') {
                // Fue un gesto del usuario: si no quedó leída, se lo decimos y la tarjeta vuelve.
                markHandled(item, false);
                toast.error(t('support_chat.incoming_ack_error'));
                return;
            }
            fetchIncoming();
        });
    };

    const dismissIncoming = (item) => {
        keepFocusOutOfLeavingCard();
        setDismissedMessageIds((prev) => new Set(prev).add(messageIdOf(item)));
    };

    const visibleIncoming = incoming.filter((i) => {
        const messageId = messageIdOf(i);
        return !dismissedMessageIds.has(messageId) && !handledMessageIds.has(messageId);
    });
    const cardItem = !open && !holdCard && visibleIncoming.length > 0 ? visibleIncoming[0] : null;
    // La región que anuncia la tarjeta está SIEMPRE montada: una región `aria-live` que se inserta ya
    // con su texto no se anuncia de forma confiable.
    const announcement = cardItem
        ? `${t('support_chat.incoming_label')}: ${incomingFromText(t, cardItem.message || {})}`
        : '';

    const showBadge = unreadCount > 0;
    const badgeLabel = unreadCount > 9 ? '9+' : String(unreadCount);

    return (
        <div className="support-chat-root">
            {open && (
                <SupportChatPanel agencyId={agencyId} onClose={closePanel}/>
            )}

            <div className="visually-hidden" role="status" data-testid="support-incoming-status">
                {announcement}
            </div>

            {cardItem && (
                <SupportIncomingCard
                    key={messageIdOf(cardItem)}
                    item={cardItem}
                    moreCount={visibleIncoming.length - 1}
                    onReply={replyToIncoming}
                    onAcknowledge={acknowledgeIncoming}
                    onDismiss={dismissIncoming}
                />
            )}

            <button
                ref={buttonRef}
                type="button"
                className={`support-chat-button ${open ? '-open' : ''}`}
                onClick={toggle}
                aria-label={t(open ? 'support_chat.close' : 'support_chat.open_label')}
                aria-expanded={open}
            >
                {open ? (
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                    </svg>
                ) : (
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
                              stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                )}
                {showBadge && !open && (
                    <span className="support-chat-button__badge" aria-hidden="true">{badgeLabel}</span>
                )}
            </button>
        </div>
    );
};
